import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Image, X, Map, Link as LinkIcon } from 'lucide-react';
import { createEvent, updateEvent, fetchRoutes, fetchEvent } from '../../api/move';
import type { MoveType, MoveLevel, MoveRouteSummary } from '../../api/move';
import { API_BASE } from '../../config';
import styles from '../../styles/Move.module.css';

function eventImageUrl(filename?: string | null) {
  if (!filename) return null;
  if (filename.startsWith('http')) return filename;
  return `${API_BASE}/api/move_images/${filename}`;
}

const TYPES: { key: MoveType; label: string }[] = [
  { key: 'walk', label: 'Caminar' },
  { key: 'run', label: 'Running' },
  { key: 'bike', label: 'Ciclismo' },
  { key: 'mixed', label: 'Mixto' },
];

const LEVELS: { key: MoveLevel; label: string }[] = [
  { key: 'iniciacion', label: 'Principiante' },
  { key: 'medio', label: 'Intermedio' },
  { key: 'avanzado', label: 'Avanzado' },
];

function formatDistance(m: number) {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`;
}

export default function EventCreatePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editId = id ? Number(id) : null;

  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [tipo, setTipo] = useState<MoveType>('run');
  const [nivel, setNivel] = useState<MoveLevel>('medio');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [inscripcionUrl, setInscripcionUrl] = useState('');
  const [plazas, setPlazas] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [precio, setPrecio] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImage, setExistingImage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [routes, setRoutes] = useState<MoveRouteSummary[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<MoveRouteSummary | null>(null);
  const [pendingRouteId, setPendingRouteId] = useState<number | null>(null);
  const [showRoutePicker, setShowRoutePicker] = useState(false);
  const [routeSearch, setRouteSearch] = useState('');

  useEffect(() => {
    fetchRoutes().then(r => setRoutes(r)).catch(() => {});
  }, []);

  // Modo edición: cargar el evento y rellenar el formulario.
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const { event } = await fetchEvent(editId);
        setTitulo(event.titulo || '');
        setDescripcion(event.descripcion || '');
        setTipo(event.tipo);
        setNivel(event.nivel);
        setFechaInicio((event.fecha_inicio || '').slice(0, 16));
        setFechaFin((event.fecha_fin || '').slice(0, 16));
        setUbicacion(event.ubicacion_nombre || '');
        setInscripcionUrl(event.inscripcion_url || '');
        setPlazas(event.plazas_max != null ? String(event.plazas_max) : '');
        setIsPremium(Boolean(Number(event.is_premium)));
        setPrecio(event.precio != null ? String(event.precio) : '');
        setExistingImage(event.imagen || null);
        setPendingRouteId(event.route_id ?? null);
      } catch {
        setError('No se pudo cargar el evento');
      }
    })();
  }, [editId]);

  // Resolver la ruta vinculada una vez cargadas las rutas.
  useEffect(() => {
    if (pendingRouteId == null || !routes.length) return;
    const r = routes.find(x => x.route_id === pendingRouteId);
    if (r) setSelectedRoute(r);
  }, [pendingRouteId, routes]);

  const filteredRoutes = routes.filter(r =>
    r.titulo.toLowerCase().includes(routeSearch.toLowerCase())
  );

  function handleImage(file: File | null) {
    setImageFile(file);
    setImagePreview(file ? URL.createObjectURL(file) : null);
  }

  async function handleSubmit() {
    setError(null);
    if (!titulo.trim()) { setError('El título es obligatorio'); return; }
    if (!fechaInicio) { setError('La fecha de inicio es obligatoria'); return; }
    const cleanInscripcionUrl = inscripcionUrl.trim();
    if (cleanInscripcionUrl && !/^https?:\/\/\S+\.\S+/i.test(cleanInscripcionUrl)) {
      setError('El link de inscripción debe empezar por http:// o https://');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        titulo: titulo.trim(),
        descripcion: descripcion.trim() ? descripcion : null,
        tipo, nivel,
        fecha_inicio: fechaInicio.replace('T', ' ') + ':00',
        fecha_fin: fechaFin ? fechaFin.replace('T', ' ') + ':00' : null,
        ubicacion_nombre: ubicacion.trim() || null,
        plazas_max: plazas ? Number(plazas) : null,
        is_premium: isPremium,
        precio: isPremium && precio ? Number(precio) : null,
        route_id: selectedRoute?.route_id ?? null,
        inscripcion_url: cleanInscripcionUrl || null,
        imagen: imageFile,
      };
      if (editId) {
        await updateEvent(editId, payload);
        navigate(`/move/events/${editId}`, { replace: true });
      } else {
        const result = await createEvent(payload);
        navigate(`/move/events/${result.event_id}`, { replace: true });
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || (editId ? 'No se pudo guardar el evento' : 'No se pudo crear el evento'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`move-app ${styles.page}`}>
      {/* Hero */}
      <header className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroOrb} />
        <div className={styles.heroInner}>
          <button
            onClick={() => navigate('/move/events')}
            style={{
              background: 'rgba(255,255,255,0.18)', border: 0, color: '#fff',
              padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
            }}
          >
            <ArrowLeft size={14} /> Eventos
          </button>
          <h1 className={styles.greetingName} style={{ marginTop: 12 }}>{editId ? 'Editar evento' : 'Crear evento'}</h1>
          <p className={styles.greetingLabel} style={{ marginTop: 4 }}>
            {editId ? 'Actualiza los datos de tu evento' : 'Organiza una actividad para la comunidad'}
          </p>
        </div>
      </header>

      {/* Imagen portada */}
      <section className={styles.section} style={{ marginTop: '-0.5rem' }}>
        {(imagePreview || existingImage) ? (
          <div style={{ position: 'relative', borderRadius: 16, overflow: 'hidden' }}>
            <img src={imagePreview || eventImageUrl(existingImage) || ''} alt="preview" style={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }} />
            <button
              type="button"
              onClick={() => { handleImage(null); setExistingImage(null); }}
              style={{
                position: 'absolute', top: 10, right: 10,
                width: 34, height: 34, borderRadius: 999, border: 0,
                background: 'rgba(0,0,0,0.5)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              }}
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <label className={styles.btnGhost} style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Image size={16} /> Añadir imagen de portada
            <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={e => handleImage(e.target.files?.[0] ?? null)} />
          </label>
        )}
      </section>

      {/* Info básica */}
      <section className={styles.section}>
        <div className={styles.card} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className={styles.label}>Título *</label>
            <input className={styles.input} value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ej. Ruta matinal por el río" />
          </div>
          <div>
            <label className={styles.label}>Descripción</label>
            <textarea className={styles.input} rows={5} value={descripcion} onChange={e => setDescripcion(e.target.value)} placeholder="Describe el evento…" style={{ resize: 'vertical', whiteSpace: 'pre-wrap' }} />
          </div>
          <div>
            <label className={styles.label}>Ubicación</label>
            <input className={styles.input} value={ubicacion} onChange={e => setUbicacion(e.target.value)} placeholder="Ej. Parque del Eresma, Segovia" />
          </div>
          <div>
            <label className={styles.label}>Link de inscripción <span style={{ fontWeight: 400, color: 'var(--move-muted-fg)' }}>(opcional)</span></label>
            <div style={{ position: 'relative' }}>
              <LinkIcon size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--move-muted-fg)' }} />
              <input
                className={styles.input}
                value={inscripcionUrl}
                onChange={e => setInscripcionUrl(e.target.value)}
                placeholder="https://..."
                inputMode="url"
                style={{ paddingLeft: 36 }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Tipo y nivel */}
      <section className={styles.section}>
        <div className={styles.card} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className={styles.label}>Tipo de actividad</label>
            <div className={styles.pillRow} style={{ padding: 0, marginTop: 8 }}>
              {TYPES.map(t => (
                <button key={t.key} className={`${styles.pill} ${tipo === t.key ? styles.pillActive : ''}`} onClick={() => setTipo(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={styles.label}>Nivel</label>
            <div className={styles.pillRow} style={{ padding: 0, marginTop: 8 }}>
              {LEVELS.map(l => (
                <button key={l.key} className={`${styles.pill} ${nivel === l.key ? styles.pillActive : ''}`} onClick={() => setNivel(l.key)}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Fechas */}
      <section className={styles.section}>
        <div className={styles.card} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className={styles.label}>Fecha y hora de inicio *</label>
            <input className={styles.input} type="datetime-local" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
          </div>
          <div>
            <label className={styles.label}>Fecha y hora de fin <span style={{ fontWeight: 400, color: 'var(--move-muted-fg)' }}>(opcional)</span></label>
            <input className={styles.input} type="datetime-local" value={fechaFin} onChange={e => setFechaFin(e.target.value)} />
          </div>
          <div>
            <label className={styles.label}>Plazas máximas <span style={{ fontWeight: 400, color: 'var(--move-muted-fg)' }}>(opcional)</span></label>
            <input className={styles.input} type="number" inputMode="numeric" value={plazas} onChange={e => setPlazas(e.target.value)} placeholder="Sin límite" />
          </div>
        </div>
      </section>

      {/* Ruta vinculada */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} style={{ marginBottom: 10 }}>Ruta vinculada</h2>
        {selectedRoute ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: 'hsl(142 76% 45% / 0.08)', border: '1px solid hsl(142 76% 45% / 0.25)',
            borderRadius: 14, padding: '12px 14px',
          }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: 'var(--move-grad-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Map size={18} color="#fff" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--move-fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedRoute.titulo}
              </p>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--move-muted-fg)' }}>
                {formatDistance(selectedRoute.distancia_m)} · +{selectedRoute.desnivel_m} m
              </p>
            </div>
            <button onClick={() => setSelectedRoute(null)} style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--move-muted-fg)', padding: 4, display: 'flex' }}>
              <X size={18} />
            </button>
          </div>
        ) : (
          <button
            className={styles.btnGhost}
            style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            onClick={() => setShowRoutePicker(true)}
          >
            <Map size={15} /> Vincular una ruta existente
          </button>
        )}
      </section>

      {/* Premium */}
      <section className={styles.section}>
        <div className={styles.card} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--move-fg)' }}>Evento de pago</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--move-muted-fg)' }}>Requiere pago para inscribirse</p>
            </div>
            {/* Toggle switch */}
            <div
              onClick={() => setIsPremium(v => !v)}
              style={{
                width: 44, height: 26, borderRadius: 999, cursor: 'pointer',
                background: isPremium ? 'var(--move-primary)' : '#d1d5db',
                position: 'relative', transition: 'background 0.2s',
                flexShrink: 0,
              }}
            >
              <div style={{
                position: 'absolute', top: 3,
                left: isPremium ? 21 : 3,
                width: 20, height: 20, borderRadius: '50%',
                background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                transition: 'left 0.2s',
              }} />
            </div>
          </div>
          {isPremium && (
            <div>
              <label className={styles.label}>Precio (€)</label>
              <input className={styles.input} type="number" step="0.01" inputMode="decimal" value={precio} onChange={e => setPrecio(e.target.value)} placeholder="0.00" />
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className={styles.section}>
        {error && <p style={{ color: 'hsl(348 90% 50%)', fontSize: 13, marginBottom: 10 }}>{error}</p>}
        <button className={styles.ctaBig} disabled={busy} onClick={handleSubmit}>
          {busy ? 'Guardando…' : editId ? 'Guardar cambios' : 'Publicar evento'}
        </button>
      </section>

      {/* Route picker sheet */}
      {showRoutePicker && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}
          onClick={() => setShowRoutePicker(false)}
        >
          <div
            style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxHeight: '72vh', display: 'flex', flexDirection: 'column', paddingBottom: 'env(safe-area-inset-bottom)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '20px 16px 12px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: '#e0e0e0', margin: '0 auto 14px' }} />
              <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 16, color: 'var(--move-fg)' }}>Seleccionar ruta</p>
              <input className={styles.input} placeholder="Buscar…" value={routeSearch} onChange={e => setRouteSearch(e.target.value)} autoFocus />
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {filteredRoutes.length === 0 && (
                <p style={{ textAlign: 'center', color: 'var(--move-muted-fg)', fontSize: 14, margin: '32px 0' }}>No hay rutas disponibles</p>
              )}
              {filteredRoutes.map(r => (
                <button
                  key={r.route_id}
                  onClick={() => { setSelectedRoute(r); setShowRoutePicker(false); setRouteSearch(''); }}
                  style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #f5f5f5', textAlign: 'left' }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: 'var(--move-grad-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Map size={18} color="#fff" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--move-fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.titulo}</p>
                    <p style={{ margin: 0, fontSize: 12, color: 'var(--move-muted-fg)' }}>{formatDistance(r.distancia_m)} · +{r.desnivel_m} m · ★ {Number(r.rating_avg).toFixed(1)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
